/**
 * User Store - Manages user profile and subscription state
 */

import { create } from 'zustand';
import { userApi, UserProfile, UsageLimits } from '../services/api';

interface UserState {
  profile: UserProfile | null;
  limits: UsageLimits | null;
  isLoading: boolean;
  error: string | null;

  limitsRequestSequence: number;
  profileRequestSequence: number;

  // Actions
  fetchProfile: (token: string, canCommit?: () => boolean) => Promise<void>;
  fetchLimits: (token: string, canCommit?: () => boolean) => Promise<void>;
  createCheckoutSession: (planType: 'monthly' | 'annual', token: string, locale?: 'ar' | 'en') => Promise<string>;
  createPortalSession: (token: string, locale?: 'ar' | 'en') => Promise<string>;
  clearError: () => void;
  clearProfile: () => void;
}

export const useUserStore = create<UserState>((set) => ({
  profile: null,
  limits: null,
  isLoading: false,
  error: null,
  limitsRequestSequence: 0,
  profileRequestSequence: 0,

  fetchProfile: async (token: string, canCommit = () => true) => {
    if (!canCommit()) return;
    const sequence = useUserStore.getState().profileRequestSequence + 1;
    set({ isLoading: true, error: null, profileRequestSequence: sequence });
    try {
      const response = await userApi.getProfile(token);
      if (!canCommit() || useUserStore.getState().profileRequestSequence !== sequence) return;
      set({ profile: response.user, isLoading: false });
    } catch (error) {
      if (!canCommit() || useUserStore.getState().profileRequestSequence !== sequence) return;
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  fetchLimits: async (token: string, canCommit = () => true) => {
    if (!canCommit()) return;
    const sequence = useUserStore.getState().limitsRequestSequence + 1;
    set({ limitsRequestSequence: sequence });
    try {
      const response = await userApi.getUsageLimits(token);
      if (!canCommit() || useUserStore.getState().limitsRequestSequence !== sequence) return;
      set({ limits: response.limits });
    } catch (error) {
      if (!canCommit()) return;
      console.error('Failed to fetch usage limits:', error);
    }
  },

  createCheckoutSession: async (planType: 'monthly' | 'annual', token: string, locale?: 'ar' | 'en') => {
    try {
      const response = await userApi.createCheckoutSession(planType, token, locale);
      return response.url;
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  createPortalSession: async (token: string, locale?: 'ar' | 'en') => {
    try {
      const response = await userApi.createPortalSession(token, locale);
      return response.url;
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  clearProfile: () => {
    set(state => ({ profile: null, limits: null, isLoading: false, error: null,
      profileRequestSequence: state.profileRequestSequence + 1,
      limitsRequestSequence: state.limitsRequestSequence + 1 }));
  },
}));
